import { useHistory } from 'react-router';
import { Header } from '../components/Header.js';
import { QuirkIonContent } from '../components/QuirkIonContent.js';
import { QuirkIonPage } from '../components/QuirkIonPage.js';
import { IconButton } from '../components/IconButton.js';
import { IconAlert, IconArrowBack, IconCheckCircle } from '../components/QuirkIcons/index.js';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { useGetOwnProfileQuery } from '../api/UserApi.js';
import { QuirkLoader } from '../components/QuirkLoader.js';
import {
	PublicSiteInformation,
	StripePaymentResponse,
	User,
	addMonths,
	formatLocalDate,
	getNow,
	isSubscriber
} from 'model';
import { IonCard, IonCardContent, IonCardTitle, IonText, useIonModal, useIonToast } from '@ionic/react';
import { Confirmation } from './Confirmation.js';
import { useGetPublicInformationQuery } from '../api/SiteSettingsAPI.js';
import styles from './Subscription.module.css';
import classNames from 'classnames';
// eslint-disable-next-line css-modules/no-unused-class
import sharedStyles from '../styles/Shared.module.css';
import { UserItem } from '../components/admin/UserItem.js';
import { useEffect, useReducer, useRef } from 'react';
import { purchaseSubscriptionReducer } from '../reducers/SubscriptionPurchaseReducer.js';
import { useGetStripeSessionQuery, useLazyStartStripeSessionQuery } from '../api/UserLedgerAPI.js';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/fetchBaseQuery.js';
import { QuirkLoading } from '../components/QuirkLoading.js';
import { usePlatform } from '../hooks/usePlatform.js';
import { getBackendURL, useBackendDomain } from '../hooks/useBackendDomain.js';

type SubscriptionModalProps = {
	user?: User;
	siteSettings?: PublicSiteInformation;
};

function SubscriptionModal({ user, siteSettings }: SubscriptionModalProps) {
	return (
		<div className={styles.subscriptionModal}>
			<div className={sharedStyles.userContainer}>{user && <UserItem user={user} modal={true} />}</div>
			<em>
				{siteSettings?.subscriptionDuration} month subscription to {siteSettings?.siteName} ending{' '}
				{formatLocalDate(addMonths(getNow(), siteSettings?.subscriptionDuration ?? 0))}
			</em>
			<h4>${siteSettings?.subscriptionCost}</h4>
		</div>
	);
}

function windowIsSameOrigin(w: Window): boolean {
	// try/catch is required
	// https://developer.mozilla.org/en-US/docs/Web/Security/Same-origin_policy
	// an exception should mean the host not the same origin
	try {
		return w.location.host === window.location.host;
	} catch (e) {
		return false;
	}
}

export function SubscriptionPage() {
	const platform = usePlatform();
	const ref = useRef<Window | null>(null);
	const history = useHistory();
	const { data: siteSettings } = useGetPublicInformationQuery();
	const { data: user, isLoading, refetch } = useGetOwnProfileQuery(useCurrentUser()._id);
	const subscriber = user ? isSubscriber(user) : undefined;
	const [startStripeSession] = useLazyStartStripeSessionQuery();
	const [state, dispatch] = useReducer(purchaseSubscriptionReducer, {});
	const { data: stripeSession } = useGetStripeSessionQuery(state.sessionId ?? '', {
		skip: !state.sessionId,
		refetchOnFocus: true
	});
	const [toast] = useIonToast();

	const onPurchase = async () => {
		hideModal();
		dispatch({ type: 'StartPurchase' });
		try {
			const { isError, error, data } = await startStripeSession({ quantity: 1, type: 'subscription' });
			if (isError) {
				dispatch({
					type: 'PurchaseError',
					error: ((error as FetchBaseQueryError)?.data as string) || 'There was an error processing payment'
				});
			} else {
				dispatch({ type: 'SessionStarted', data: data as StripePaymentResponse });
			}
		} catch (e) {
			dispatch({
				type: 'PurchaseError',
				error: e instanceof Error ? e : new Error(String(e))
			});
		}
	};

	const [showModal, hideModal] = useIonModal(Confirmation, {
		onDismiss: () => hideModal(),
		action: onPurchase,
		actionLabel: 'Purchase Subscription',
		dismissLabel: 'Cancel',
		content: <SubscriptionModal user={user} siteSettings={siteSettings} />
	});

	useEffect(() => {
		if (stripeSession) {
			dispatch({ type: 'SessionLoaded', data: stripeSession });
		}
	}, [stripeSession]);

	useEffect(() => {
		if (state.error) {
			toast({
				color: 'danger',
				message: `Failed to purchase subscription: ${
					state.error instanceof Error ? state.error.message : state.error
				}`,
				position: 'top',
				duration: 1500
			});
		}
	}, [state.error, toast]);

	useEffect(() => {
		if (state.stripeSession?.url && !ref.current && state.status === 'started') {
			ref.current = window.open(state.stripeSession?.url, '_blank');
			dispatch({ type: 'StripeLaunched' });
		}
		let handle: ReturnType<typeof setInterval> | undefined = setInterval(() => {
			if (ref.current?.closed) {
				clearInterval(handle);
				handle = undefined;
				const canceled = !windowIsSameOrigin(ref.current);
				dispatch({ type: 'StripeClosed', canceled });
				ref.current = null;
			}
		}, 100);
		return () => {
			if (handle) {
				clearInterval(handle);
			}
		};
	}, [state.stripeSession?.url, state.status]);

	useEffect(() => {
		if (state.stripeSession?.status === 'complete') {
			dispatch({ type: 'CompletePurchase' });
			refetch();
		}
	}, [state.stripeSession?.status, refetch]);
	const [domain] = useBackendDomain();

	const onPurchaseClick = () => {
		if (platform === 'web') {
			// On web show the confirmation modal
			showModal();
		} else {
			// On mobile open the subscription page in a browser
			window.open(getBackendURL('main/settings/subscription', domain), '_blank');
		}
	};

	return (
		<QuirkIonPage name="My Subscription">
			<QuirkIonContent flush>
				<>
					<Header
						left={<IconButton icon={<IconArrowBack />} onClick={history.goBack} />}
						title="My Subscription"
					/>
					<QuirkLoader data={user} isLoading={isLoading} noResults={<>Failed to load.</>}>
						<IonCard>
							<IonCardContent>
								<div className={styles.subscriptionStatus}>
									<span
										className={classNames({
											[styles.statusIcon]: true,
											[styles.subscriber]: subscriber
										})}
									>
										{subscriber && user && <IconCheckCircle size="xlarge" />}
										{!subscriber && user && <IconAlert size="xlarge" />}
									</span>
									<IonCardTitle data-testid="SubscriptionStatusText">
										{subscriber && user ? 'Active' : 'Not Active'}
									</IonCardTitle>
									<IonText>
										{subscriber && user && (
											<>
												Your subscription is currently active and ends{' '}
												{formatLocalDate(user.subscriptionEndDate)}
											</>
										)}
										{!subscriber && user && <>No active subscription found.</>}
									</IonText>
									{!subscriber && user && (
										<IconButton
											icon={<IconCheckCircle color="var(--primary)" />}
											onClick={onPurchaseClick}
											label="Purchase Subscription"
											iconRight
											outline
											disabled={
												state.stripeOpen ||
												(state.status !== undefined && state.status !== 'error')
											}
											testId="PurchaseSubscriptionButton"
										/>
									)}
									<QuirkLoading
										isLoading={
											state.status !== undefined &&
											state.status !== 'error' &&
											state.status !== 'complete'
										}
										message="Complete purchase with Stripe"
									/>
								</div>
							</IonCardContent>
						</IonCard>
					</QuirkLoader>
				</>
			</QuirkIonContent>
		</QuirkIonPage>
	);
}
